/**
 * HostPoint POS - PWA Hooks
 * React hooks for PWA functionality including network status, install prompt, and sync
 */

import { useState, useEffect, useCallback } from 'react';
import offlineDB from './offline-storage';

/**
 * Hook to track online/offline status
 */
export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOffline) {
        // Trigger sync when coming back online
        triggerSync();
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  const triggerSync = useCallback(async () => {
    if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.sync.register('sync-orders');
      } catch (error) {
      }
    }
  }, []);

  return { isOnline, wasOffline, triggerSync };
}

/**
 * Hook for PWA install prompt
 */
export function useInstallPrompt() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!installPrompt) return false;

    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setInstallPrompt(null);
      setIsInstallable(false);
    }
    
    return outcome === 'accepted';
  }, [installPrompt]);

  return { isInstallable, isInstalled, promptInstall };
}

/**
 * Hook for sync status
 */
export function useSyncStatus() {
  const [syncStatus, setSyncStatus] = useState({
    pendingOrders: 0,
    pendingPayments: 0,
    queuedItems: 0,
    failedItems: 0,
    lastSyncAttempt: null,
    isOnline: navigator.onLine
  });
  const [isSyncing, setIsSyncing] = useState(false);

  const refreshStatus = useCallback(async () => {
    try {
      const status = await offlineDB.getSyncStatus();
      setSyncStatus(status);
    } catch (error) {
    }
  }, []);

  useEffect(() => {
    refreshStatus();
    
    // Refresh status periodically
    const interval = setInterval(refreshStatus, 30000);
    
    // Also refresh on network status change
    const handleOnline = () => refreshStatus();
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOnline);

    return () => {
      clearInterval(interval);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOnline);
    };
  }, [refreshStatus]);

  const manualSync = useCallback(async (apiUrl, token) => {
    if (isSyncing) return;
    
    setIsSyncing(true);
    try {
      const results = await offlineDB.syncAll(apiUrl, token);
      await refreshStatus();
      return results;
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, refreshStatus]);

  return { syncStatus, isSyncing, manualSync, refreshStatus };
}

/**
 * Hook for service worker registration and updates
 */
export function useServiceWorker() {
  const [registration, setRegistration] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    let updateTimer = null;

    const watchWorker = (worker) => {
      if (!worker) return;
      worker.addEventListener('statechange', () => {
        if (worker.state === 'installed' && navigator.serviceWorker.controller) {
          setUpdateAvailable(true);
        }
      });
    };

    navigator.serviceWorker.register('/sw.js')
      .then((reg) => {
        setRegistration(reg);

        // A new version may already be sitting in "waiting" from a previous visit
        if (reg.waiting && navigator.serviceWorker.controller) {
          setUpdateAvailable(true);
        }
        watchWorker(reg.installing);
        reg.addEventListener('updatefound', () => watchWorker(reg.installing));

        // Tills stay open for days - poll for a new build every 30 minutes
        updateTimer = setInterval(() => reg.update().catch(() => {}), 30 * 60 * 1000);
      })
      .catch(() => {});

    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'ORDER_SYNCED') {
        // Could dispatch an event or update state here
      }
    });

    return () => {
      if (updateTimer) clearInterval(updateTimer);
    };
  }, []);

  const updateApp = useCallback(() => {
    if (!registration?.waiting) return;
    // Reload only once the new worker has taken control, otherwise the reload
    // can still be served by the old version.
    navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), { once: true });
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    setTimeout(() => window.location.reload(), 3000);
  }, [registration]);

  return { registration, updateAvailable, updateApp };
}

/**
 * Hook for caching POS data
 */
export function usePOSCache() {
  const [isCached, setIsCached] = useState(false);
  const [lastCacheTime, setLastCacheTime] = useState(null);

  useEffect(() => {
    // Check if we have cached data
    const checkCache = async () => {
      try {
        const products = await offlineDB.getCachedProducts();
        const categories = await offlineDB.getCachedCategories();
        
        if (products.length > 0 && categories.length > 0) {
          setIsCached(true);
          const latestCache = [...products, ...categories]
            .map(item => item.cached_at)
            .sort()
            .pop();
          setLastCacheTime(latestCache);
        }
      } catch (error) {
      }
    };
    
    checkCache();
  }, []);

  const cacheProducts = useCallback(async (products) => {
    try {
      await offlineDB.cacheProducts(products);
      setIsCached(true);
      setLastCacheTime(new Date().toISOString());
    } catch (error) {
    }
  }, []);

  const cacheCategories = useCallback(async (categories) => {
    try {
      await offlineDB.cacheCategories(categories);
    } catch (error) {
    }
  }, []);

  const getCachedProducts = useCallback(async (propertyId) => {
    try {
      return await offlineDB.getCachedProducts(propertyId);
    } catch (error) {
      return [];
    }
  }, []);

  const getCachedCategories = useCallback(async (propertyId) => {
    try {
      return await offlineDB.getCachedCategories(propertyId);
    } catch (error) {
      return [];
    }
  }, []);

  return { 
    isCached, 
    lastCacheTime, 
    cacheProducts, 
    cacheCategories,
    getCachedProducts,
    getCachedCategories
  };
}

export default {
  useNetworkStatus,
  useInstallPrompt,
  useSyncStatus,
  useServiceWorker,
  usePOSCache
};
